import { BlockComponentFactory } from '../BlockFactory'
import Select from 'antd/lib/select'
import { DropdownBlockProps } from './block-props.types'
import { useCallback, useMemo } from 'react'
import { LabelBlock } from './LabelBlock'
import { LABEL_FONT_STYLE } from '../../constants'
import { useBlockListeners } from '../BlockHooks'
import { useBlockEngine } from '../BlockEngineContext'
import { toCSSSize } from './block-utils'

export const DropdownBlockComponent =
    BlockComponentFactory.createBlockComponent<DropdownBlockProps>(props => {
        const {
            label,
            infoText,
            required,
            options = [],
            mode = 'single',
            placeholder,
            width,
            visibility = 'visible',
            disabled = false,
            style,
            value,
        } = props

        const isEditable = useBlockEngine().blockEngine.getEditableSignal().value

        const [onChangeListener] = useBlockListeners<(value: string[]) => void>('onChange')

        const isMultiple = mode === 'multiple'

        const containerStyle: React.CSSProperties = {
            ...style,
            display: visibility === 'hidden' ? 'none' : 'flex',
            flexDirection: 'column',
            gap: 8,
            width: toCSSSize(width) ?? 'auto',
        }

        const selectOptions = useMemo(
            () =>
                options.map(opt => ({
                    label: opt.value,
                    value: opt.value,
                })),
            [options]
        )

        const validOptionValues = useMemo(() => new Set(options.map(o => o.value)), [options])

        const defaultValue = useMemo(() => {
            if (isMultiple) {
                return options.filter(o => o.checked).map(o => o.value)
            }

            return options.find(o => o.checked)?.value
        }, [options, isMultiple])

        const normalizedValue = useMemo(() => {
            if (!value) return undefined

            if (isMultiple) {
                if (Array.isArray(value)) return value

                if (typeof value === 'string') {
                    return value
                        .split(',')
                        .map(v => v.trim())
                        .filter(Boolean)
                }

                return undefined
            }

            return typeof value === 'string' ? value : undefined
        }, [value, isMultiple])

        const isValidValue = useMemo(() => {
            if (!normalizedValue) return false

            if (isMultiple) {
                return (
                    Array.isArray(normalizedValue) &&
                    normalizedValue.every(v => validOptionValues.has(v))
                )
            }

            return validOptionValues.has(normalizedValue as string)
        }, [normalizedValue, isMultiple, validOptionValues])

        const selectedValue = isValidValue ? normalizedValue : defaultValue

        const handleChange = useCallback(
            (val: string | string[]) => {
                const newValue = isMultiple ? (val as string[]) : [val as string]

                onChangeListener?.value?.(newValue)
            },
            [isMultiple, onChangeListener]
        )

        return (
            <div style={containerStyle}>
                <LabelBlock
                    label={label}
                    required={required}
                    infoText={infoText}
                    labelStyle={LABEL_FONT_STYLE}
                />
                <Select
                    mode={isMultiple ? 'multiple' : undefined}
                    options={selectOptions}
                    value={selectedValue}
                    placeholder={placeholder ?? 'Select an option'}
                    disabled={!isEditable || disabled}
                    onChange={handleChange}
                />
            </div>
        )
    })
